import { Link } from "react-router-dom";

import { PRICING_PATH } from "@/lib/auth/auth-paths";
import { useBillingStatusQuery } from "@/hooks/use-billing";
import { Button } from "@/components/ui/button";

interface SubscriptionBannerProps {
  // "card" sits in the desktop sidebar; "strip" spans the top of the page on
  // mobile, where the sidebar is hidden in the drawer.
  variant?: "card" | "strip";
}

/**
 * The dashboard stays open without a plan (the API gates only sending
 * notifications and data source URLs outside appio), so this is the only
 * standing prompt to subscribe. Shown only on a successful "not entitled"
 * answer, so a paying customer never sees it flash up while loading or after
 * a failed request.
 */
export function SubscriptionBanner({ variant = "card" }: SubscriptionBannerProps) {
  const billingStatus = useBillingStatusQuery();

  if (!billingStatus.isSuccess || billingStatus.data.entitled) {
    return null;
  }

  if (variant === "strip") {
    return (
      // Black in both themes, so it stands apart from the page beneath.
      <div className="flex items-center gap-3 bg-neutral-950 px-4 py-2.5 text-white">
        <p className="min-w-0 flex-1 text-[13px] leading-snug">
          <span className="font-medium">No active subscription.</span>{" "}
          <span className="text-white/70">
            Choose a plan to send notifications and connect your own data sources.
          </span>
        </p>
        <Button
          asChild
          size="sm"
          className="shrink-0 bg-white text-neutral-950 shadow-none hover:bg-white/85 focus-visible:ring-white focus-visible:ring-offset-neutral-950"
        >
          <Link to={PRICING_PATH}>See pricing</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-2 rounded-xl bg-background p-4 shadow-card">
      <p className="text-[13px] font-medium">No active subscription</p>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        Choose a plan to send notifications and connect your own data sources.
      </p>
      <Button asChild size="sm" className="mt-3 w-full">
        <Link to={PRICING_PATH}>See pricing</Link>
      </Button>
    </div>
  );
}
